// The home page's Recently added shelf (Oct 2026, user: "wonder if we need a
// new / recently added tunes section on the homepage"): the scores that ship
// with the app and the tunes an admin promoted to the library, newest first,
// so a new transcription can be found without knowing it is there. A score
// is dated by `addedAt` on its catalogue entry, a library tune by the day it
// was promoted. Only the last RECENT_DAYS count, so the shelf goes away when
// nothing is new rather than going stale.

import { BUNDLED_SCORES } from './bundledScores';
import type { LibraryTuneEntry } from './libraryTunes';
import { scoreCatalogKey, type ScoreCatalogEntry } from './scoreCatalog';
import { collectTuneSources, type TuneSourceEntry } from './tuneSources';

/** How far back the shelf looks. */
export const RECENT_DAYS = 60;
/** The most tunes it shows. */
export const RECENT_MAX = 10;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface RecentlyAddedTune {
  row: TuneSourceEntry;
  /** When it was added, as given (a date, or a timestamp for a library tune). */
  addedAt: string;
}

export function recentlyAddedTunes({
  bundledScores = BUNDLED_SCORES,
  library = [],
  now,
}: {
  bundledScores?: readonly ScoreCatalogEntry[];
  library?: readonly LibraryTuneEntry[];
  /** Milliseconds since the epoch. */
  now: number;
}): RecentlyAddedTune[] {
  const rows = new Map(
    collectTuneSources({
      presets: [],
      bundledScores,
      savedCharts: [],
      openbook: [],
      library: [...library],
      userPlaylists: [],
      playlist: null,
    }).map((row) => [row.key, row]),
  );
  // A score's row key is its catalogue key; a library tune's is library-<id>.
  const dated = [
    ...bundledScores
      .filter((entry) => entry.listing.status === 'active')
      .map((entry) => ({
        key: scoreCatalogKey(entry),
        addedAt: entry.addedAt,
      })),
    ...library.map((entry) => ({ key: `library-${entry.id}`, addedAt: entry.createdAt })),
  ];
  const since = now - RECENT_DAYS * DAY_MS;
  const out: (RecentlyAddedTune & { time: number })[] = [];
  for (const { key, addedAt } of dated) {
    const time = Date.parse(addedAt);
    const row = rows.get(key);
    if (!row || !Number.isFinite(time) || time < since || time > now + DAY_MS) continue;
    out.push({ row, addedAt, time });
  }
  // Newest first; a stable sort keeps the catalogue's order within a day.
  return out
    .sort((a, b) => b.time - a.time)
    .slice(0, RECENT_MAX)
    .map(({ row, addedAt }) => ({ row, addedAt }));
}

/** "Added 10 Oct" (the shelf only reaches back two months, so no year). */
export function addedLabel(addedAt: string): string {
  const time = Date.parse(addedAt);
  if (!Number.isFinite(time)) return 'Added';
  const day = new Date(time).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  return `Added ${day}`;
}
